/**
 * TEMPORARY local contracts mirror for the coarse jobs DTOs.
 *
 * Plan 22-10 added the `/api/v1/jobs` + `/api/v1/jobs/:id` profitability surface to
 * `@hbs-us/contracts` SOURCE (now 1.7.0), but the dashboard's INSTALLED `@hbs-us/contracts`
 * predates it. Republishing the package is owner-gated CI (gitleaks/license/cosign, GitHub
 * Packages) and cannot run here. Following the established Phase 19 + 21 + 22-08/09 pattern,
 * these types are mirrored locally, shaped IDENTICALLY to the coarse responses built in
 * `qelm-backend/workers/api/src/routes/api.ts`, and marked with the carry below.
 *
 * TODO(contracts republish): delete this file and import `JobDto`, `JobFinancialsDto`,
 * `JobProfitabilityDto`, `JobsListResponse`, `JobDetailResponse`, `JobBlueprintDto` from
 * `@hbs-us/contracts` once the jobs surface is published and pinned in this repo's package.json.
 */

/** The 4 job statuses the legacy app allows (carve ALLOWED_JOB_STATUSES). Carried as TEXT. */
export type JobStatus = 'Active' | 'Completed' | 'On Hold' | 'Cancelled';

export const ALLOWED_JOB_STATUSES: readonly JobStatus[] = [
  'Active',
  'Completed',
  'On Hold',
  'Cancelled',
];

/** Per-job financial rollups (server-computed; the SPA never re-aggregates). */
export interface JobFinancialsDto {
  totalIncome: number;
  totalExpenses: number;
  totalLabor: number;
  totalHours: number;
  totalCosts: number;
  totalInvoiced: number;
  totalCollected: number;
  unpaidInvoices: number;
  unpaidInvoiceBalance: number;
  remainingContract: number;
  profit: number;
}

/** A job in the coarse list (and the base of the detail payload). */
export interface JobDto {
  id: number;
  jobName: string | null;
  jobCode: string | null;
  jobDescription: string | null;
  clientName: string | null;
  startDate: string | null;
  status: JobStatus | string | null;
  isOverhead: boolean;
  sourceEstimateId: number | null;
  sourceEstimateNumber: string | null;
  sourceEstimateCustomerName: string | null;
  soldBy?: string | null;
  commissionPercent?: number;
  contractAmount?: number;
  retainagePercent?: number;
  financials?: JobFinancialsDto;
}

/**
 * The per-job Profitability aggregate (D-05). Computed server-side by the golden
 * job-profit-calc from CORE-D1 inputs ONLY — never the payments vault. The SPA renders
 * these numbers verbatim and NEVER re-aggregates them.
 */
export interface JobProfitabilityDto {
  totalIncome: number;
  totalExpenses: number;
  totalLabor: number;
  totalHours: number;
  contractAmount: number;
  retainagePercent: number;
  totalCosts: number;
  profit: number;
  profitMargin: number;
  remainingContract: number;
  retainageHeld: number;
}

/** A job blueprint (coarse list item). */
export interface JobBlueprintDto {
  id: number;
  jobId: number;
  title: string;
  notes: string | null;
  originalFilename: string | null;
  uploadedByName: string | null;
  createdAt: string | null;
  archivedAt: string | null;
}

/** Shared API envelope shape (matches `@hbs-us/contracts` `ApiEnvelope`). */
interface ApiEnvelope {
  ok: boolean;
  error?: string;
}

/** Server-computed status counts for the SPA pills (one count per allowed status). */
export type JobStatusCounts = Record<JobStatus, number>;

/** Coarse `GET /api/v1/jobs` response (22-10): jobs + status counts. */
export interface JobsListResponse extends ApiEnvelope {
  jobs?: JobDto[];
  statusCounts?: JobStatusCounts;
  total?: number;
}

/** `GET /api/v1/jobs/:id` response (22-10): the job + its core-D1 profitability aggregate. */
export interface JobDetailResponse extends ApiEnvelope {
  job?: JobDto;
  profitability?: JobProfitabilityDto;
}

/** `GET /api/v1/jobs/:id/blueprints` response. */
export interface JobBlueprintsResponse extends ApiEnvelope {
  blueprints?: JobBlueprintDto[];
}

/**
 * The additive create/edit job write request (22-22). Mirrors the camelCase body
 * `POST /api/v1/jobs` + `PATCH /api/v1/jobs/:id` accept. `commissionPercent` + `retainagePercent`
 * are the fields the reachable form now persists (the mobile-shaped handler previously dropped
 * them). The client NEVER sends a tenant id (SEC-11).
 */
export interface JobWriteRequest {
  jobName?: string | null;
  jobCode?: string | null;
  clientName?: string | null;
  soldBy?: string | null;
  jobDescription?: string | null;
  status?: JobStatus | string | null;
  isOverhead?: boolean;
  contractAmount?: number | null;
  commissionPercent?: number | null;
  retainagePercent?: number | null;
  startDate?: string | null;
}

/** `POST /api/v1/jobs` response (22-22): the new id + an echo of the persisted job. */
export interface JobCreateResponse extends ApiEnvelope {
  jobId?: number;
  job?: {
    id: number;
    jobName: string | null;
    jobCode: string | null;
    status: JobStatus | string;
    commissionPercent: number;
    retainagePercent: number;
    contractAmount: number | null;
  };
}

/** The additive `job_code_exists` error code the write surface returns on a duplicate (409). */
export const JOB_CODE_EXISTS_ERROR = 'job_code_exists';

/** An income row on a job (GET /api/v1/jobs/:id/income). */
export interface JobIncomeRow {
  id: number;
  jobId: number;
  amount: number;
  date: string;
  description: string | null;
  archivedAt?: string | null;
}

/** `GET /api/v1/jobs/:id/income` response (22-22): active rows + the additive archived rows. */
export interface JobIncomeResponse extends ApiEnvelope {
  income?: JobIncomeRow[];
  archivedIncome?: JobIncomeRow[];
}

/** An expense row on a job (GET /api/v1/jobs/:id/expenses). */
export interface JobExpenseRow {
  id: number;
  jobId: number;
  category: string | null;
  vendor: string | null;
  amount: number;
  date: string;
  archivedAt?: string | null;
  /** True when a receipt image is stored for this expense. The server sends a boolean, never
   *  the filename — that value is the R2 object key. Optional so an older API still parses. */
  hasReceipt?: boolean;
}

/** `GET /api/v1/jobs/:id/expenses` response (22-22): active rows + the additive archived rows. */
export interface JobExpensesResponse extends ApiEnvelope {
  expenses?: JobExpenseRow[];
  archivedExpenses?: JobExpenseRow[];
}

/** A time entry (labor) row on a job (GET /api/v1/jobs/:id/time-entries). */
export interface JobTimeEntryRow {
  id: number;
  employeeId: number | null;
  employeeName: string | null;
  date: string;
  hours: number;
  note: string | null;
  entryMethod: string | null;
}

/** `GET /api/v1/jobs/:id/time-entries` response: the labor entries for the Labor tab. */
export interface JobTimeEntriesResponse extends ApiEnvelope {
  entries?: JobTimeEntryRow[];
}

/** The additive expense-edit request (PATCH /api/v1/expenses/:id — full field shape). */
export interface ExpenseEditRequest {
  category: string;
  vendor: string | null;
  amount: number;
  date: string;
}
